import { useState } from "react";
import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type { GuestsSelection } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  type ChildAgePickerComponentProps,
  GuestCountPicker,
  useChildAgePicker,
  useGuestPicker,
} from "@tenant/hotels";
import { InputContainer, Select, Dropdown, Icon, SelectItem } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./GuestPicker.module.scss";

export interface GuestPickerComponentProps extends PlatformComponentProps {
  onGuestSelectionHandler: (guests: GuestsSelection, rooms?: number) => void;
  guests: GuestsSelection;
  rooms?: number;
  onRoomsSelectionHandler?: (rooms: number) => void;
  config?: {
    enableRoomSelection: boolean;
  };
}

export const GuestPicker = ({
  className,
  ...rest
}: GuestPickerComponentProps) => {
  const COMPONENT_KEY = CoreHotelsComponent.GuestPicker;
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { handlers, text } = useGuestPicker(rest);
  const { t } = useI18nContext();
  const [open, setOpen] = useState(false);

  const onOpenChange = (open: boolean) => {
    handlers.onOpenChange(open);
    setOpen(open);
  };

  const onApply = () => {
    setOpen(false);
  };

  return (
    <Dropdown
      onOpenChange={onOpenChange}
      modal={false}
      open={open}
      disablePortal
      className={clsx(cn("dropdown"), className)}
      triggerContent={
        <InputContainer className={cn("button")} id="guest-picker-button">
          <div className={cn("button-content")}>
            <Icon className={cn("icon")} iconName={IconNameEnum.guest} />
            <div className={cn("button-label")}>
              <div className={cn("button-label-text")}>
                {t("core-hotels.search.guestPicker.label")}
              </div>
              <div className={cn("button-value-text")}>{text.buttonLabel}</div>
            </div>
          </div>
        </InputContainer>
      }
    >
      <GuestPickerContent {...rest} onApply={onApply} />
    </Dropdown>
  );
};

const GuestPickerContent = (
  props: GuestPickerComponentProps & { onApply: () => void }
) => {
  const COMPONENT_KEY = CoreHotelsComponent.GuestPicker;
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { context, handlers, text } = useGuestPicker(props);

  return (
    <div className={cn("dialog-content")}>
      <div className={cn("action-content")}>
        <GuestCountPicker
          title={text.adultTitle}
          minimumCount={context.minimumAdultCount}
          count={context.adultCount}
          setCount={handlers.onAdultChange}
          maximumReached={context.maximumReached}
          className={cn("guest-count-picker")}
        />
        <GuestCountPicker
          title={text.childCountTitle}
          subtitle={text.childCountSubtitle}
          minimumCount={0}
          count={context.childCount}
          setCount={(count) => handlers.setChildCount(count)}
          maximumReached={context.maximumReached}
          className={cn("guest-count-picker")}
        />
        {context.childCount > 0 && (
          <div className={cn("child-age-pickers-container")}>
            {Array.from({ length: context.childCount }).map((_, index) => {
              return (
                <ChildAgePicker
                  key={index}
                  onAgeSelectHandler={(age: number) => {
                    handlers.updateChildAge(index, age);
                  }}
                  index={index}
                  childAge={context.childAgeArr[index]}
                  label={text.childAgeLabel(index)}
                />
              );
            })}
          </div>
        )}
        <div className={cn("apply-button-container")}>
          <button
            disabled={!context.isGuestPickerValid}
            className={cn("apply-button")}
            onClick={() => {
              handlers.onGuestSelection({
                adults: context.adultCount,
                children: context.childAgeArr,
              });
              props.onApply();
            }}
          >
            {text.applyButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

interface ChildAgePickerProps extends ChildAgePickerComponentProps {
  childAge: number;
  index: number;
  onAgeSelectHandler: (age: number) => void;
}

const ChildAgePicker = (props: ChildAgePickerProps) => {
  const COMPONENT_KEY = CoreHotelsComponent.ChildAgePicker;
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block] = useModuleBEM(styles, COMPONENT_KEY);

  return (
    <div className={block}>
      <div>
        <ChildAgeSelect
          index={props.index}
          onAgeSelectHandler={props.onAgeSelectHandler}
          childAge={props.childAge}
        />
      </div>
    </div>
  );
};

const ChildAgeSelect = (props: ChildAgePickerProps) => {
  const { childAge, onAgeSelectHandler } = props;
  const COMPONENT_KEY = CoreHotelsComponent.ChildAgePicker;
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();

  const childAgePickerProps = {
    onAgeSelectHandler,
    childAge,
  };

  const { context } = useChildAgePicker(childAgePickerProps);

  const options = context.ageOptions.map((option) => {
    return (
      <SelectItem key={option.key} value={option.value}>
        {option.label}
      </SelectItem>
    );
  });

  const name = `${props.index}-child-age`;
  return (
    <div className={cn("child-age-select-container")}>
      <label htmlFor={name} className={cn("label")}>
        {t("core-hotels.search.guestPicker.child.selectPlaceHolder")}
      </label>
      <Select
        name={name}
        disablePortal
        value={context.childAgeString}
        onValueChange={(value) => {
          onAgeSelectHandler(Number(value));
        }}
      >
        {options}
      </Select>
    </div>
  );
};
